import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const OrderList = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("All");
    const [search, setSearch] = useState("");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await fetch(
                "https://tailor.beezetsoftwaresolution.com/api/order/get_orders.php"
            );
            const data = await response.json();

            if (data && Array.isArray(data)) {
                setOrders(data);
            } else {
                console.error("Invalid response format:", data);
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredOrders = orders.filter(
        (o) =>
            (activeTab === "All" || o.status === activeTab) &&
            (o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
                o.order_id?.toString().includes(search) ||
                o.phone?.includes(search))
    );

    const badgeColors = {
        "On Order": "bg-blue-800 text-white",
        Processing: "bg-blue-500 text-white",
        Trial: "bg-blue-400 text-white",
        Completed: "bg-green-500 text-white",
    };

    // Updated function to pass complete order object
    const handleEditOrder = (order) => {
        console.log(order)

        navigate(`/edit-order?id=${order.id}`, {
            state: { orderData: order }
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen text-blue-700 font-semibold">
                Loading orders...
            </div>
        );
    }

    return (
        <div className="bg-blue-50 min-h-screen p-4">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-blue-800 mb-2">Order Listing</h1>
                <p className="text-blue-600">Manage and track all your orders</p>
            </div>

            {/* Search Input */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
                <input
                    type="text"
                    placeholder="Search by customer, order ID, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:max-w-md p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-700 focus:outline-none"
                />
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
                {["All", "On Order", "Processing", "Trial", "Completed"].map(
                    (tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-1 text-sm font-medium rounded-md border transition-all ${activeTab === tab
                                ? "bg-blue-700 text-white border-blue-700"
                                : "bg-white text-blue-700 border-blue-300 hover:bg-blue-100"
                                }`}
                        >
                            {tab} ({orders.filter(o => tab === "All" || o.status === tab).length})
                        </button>
                    )
                )}
            </div>

            {/* Orders Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredOrders.map((order) => (
                    <div
                        key={order.order_id}
                        className="relative bg-white border border-blue-200 rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow"
                    >
                        {/* Status Badge */}
                        <div
                            className={`absolute top-2 right-2 text-xs font-semibold px-2 py-1 rounded ${badgeColors[order.status] || "bg-gray-300 text-gray-700"
                                }`}
                        >
                            {order.status}
                        </div>

                        {/* Header */}
                        <div className="flex justify-between items-center mb-1">
                            <h2 className="font-semibold text-blue-800">#{order.order_id}</h2>
                            <button
                                onClick={() => setSelectedOrder(order)}
                                className="text-xs text-blue-500 hover:text-blue-700 transition-colors"
                            >
                                👁️ Details
                            </button>
                        </div>

                        {/* Customer Info */}
                        <p className="text-blue-700 text-sm font-medium">
                            {order.customer_name}
                        </p>
                        <p className="text-sm text-blue-500 mb-3">📱 {order.phone}</p>

                        {/* Employee + Delivery Date */}
                        <div className="flex justify-between text-sm text-blue-600 mb-2">
                            <p>👔 {order.employee_name || "N/A"}</p>
                            <p>📦 {order.delivery_date}</p>
                        </div>

                        {/* Costumes */}
                        {order.costumes && Array.isArray(order.costumes) && order.costumes.length > 0 && (
                            <div className="bg-blue-50 rounded-md p-2 text-sm mb-3">
                                <p className="font-medium text-blue-700 mb-1">👕 Costumes</p>
                                {order.costumes.slice(0, 2).map((c, i) => (
                                    <p key={i} className="text-blue-600">
                                        • {c.costume_type} (Qty: {c.quantity})
                                    </p>
                                ))}
                                {order.costumes.length > 2 && (
                                    <p className="text-blue-500 text-xs mt-1">
                                        +{order.costumes.length - 2} more...
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Amount Details */}
                        <div className="flex justify-between text-sm font-medium mb-4">
                            <div className="text-blue-700">
                                <span className="text-xs text-gray-500">Total</span>
                                <br />₹{order.total_amount}
                            </div>
                            <div className="text-green-600 text-right">
                                <span className="text-xs text-gray-500">Paid</span>
                                <br />₹{order.advance_payment}
                            </div>
                            <div className="text-red-500 text-right">
                                <span className="text-xs text-gray-500">Balance</span>
                                <br />₹{order.total_amount - order.advance_payment}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleEditOrder(order)}
                                className="flex-1 bg-orange-600 text-white py-2 text-sm rounded-md hover:bg-orange-700 transition-all"
                            >
                                ✏️ Edit
                            </button>
                            <button className="flex-1 bg-blue-700 text-white py-2 text-sm rounded-md hover:bg-blue-800 transition-all">
                                📋 Manage
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {filteredOrders.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">📦</div>
                    <p className="text-blue-700 font-medium text-lg">No orders found</p>
                    <p className="text-blue-500 text-sm mt-2">
                        {search ? "Try adjusting your search" : "Start by creating a new order"}
                    </p>
                </div>
            )}

            {/* ORDER DETAILS MODAL */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center px-4 z-50">
                    <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-5 relative max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={() => setSelectedOrder(null)}
                            className="absolute top-2 right-2 text-xl text-red-500 font-bold hover:text-red-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors"
                        >
                            ×
                        </button>

                        <h2 className="text-xl font-semibold text-blue-800 mb-4">
                            Order Details – #{selectedOrder.order_id}
                        </h2>

                        {/* Status Badge in Modal */}
                        <div className={`inline-block text-xs font-semibold px-3 py-1 rounded mb-4 ${badgeColors[selectedOrder.status] || "bg-gray-300 text-gray-700"
                            }`}>
                            {selectedOrder.status}
                        </div>

                        <div className="space-y-4">
                            {/* Customer Info */}
                            <div className="bg-blue-50 p-3 rounded-md">
                                <p className="font-medium text-blue-700 mb-1">Customer Information</p>
                                <p className="font-semibold">{selectedOrder.customer_name}</p>
                                <p className="text-blue-500">📱 {selectedOrder.phone}</p>
                            </div>

                            {/* Employee Info */}
                            <div>
                                <p className="font-medium text-blue-700 mb-1">Assigned Employee</p>
                                <p>{selectedOrder.employee_name || "Not assigned"}</p>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="font-medium text-blue-700 text-sm">Delivery Date</p>
                                    <p className="text-sm">📅 {selectedOrder.delivery_date || "Not set"}</p>
                                </div>
                                {selectedOrder.trial_date && (
                                    <div>
                                        <p className="font-medium text-blue-700 text-sm">Trial Date</p>
                                        <p className="text-sm">📅 {selectedOrder.trial_date}</p>
                                    </div>
                                )}
                            </div>

                            {/* Costumes */}
                            {selectedOrder.costumes && selectedOrder.costumes.length > 0 && (
                                <div className="bg-blue-50 p-3 rounded-md">
                                    <p className="font-medium text-blue-700 mb-2">Costumes</p>
                                    {selectedOrder.costumes.map((c, i) => (
                                        <div key={i} className="mb-2 last:mb-0">
                                            <p className="font-medium">• {c.costume_type}</p>
                                            <p className="text-sm text-gray-600 ml-4">
                                                Quantity: {c.quantity}
                                                {c.assigned_employee_name && ` • Tailor: ${c.assigned_employee_name}`}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Payment Details */}
                            <div className="bg-gray-50 p-3 rounded-md">
                                <p className="font-medium text-blue-700 mb-2">Payment Information</p>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-xs text-gray-500">Total Amount</p>
                                        <p className="font-semibold text-blue-700">₹{selectedOrder.total_amount}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Paid</p>
                                        <p className="font-semibold text-green-600">₹{selectedOrder.advance_payment}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Balance</p>
                                        <p className="font-semibold text-red-500">
                                            ₹{selectedOrder.total_amount - selectedOrder.advance_payment}
                                        </p>
                                    </div>
                                </div>
                                {selectedOrder.payment_mode && (
                                    <p className="text-sm text-center mt-2 text-gray-600">
                                        Payment Mode: {selectedOrder.payment_mode}
                                    </p>
                                )}
                            </div>

                            {/* Notes */}
                            {selectedOrder.notes && (
                                <div>
                                    <p className="font-medium text-blue-700 mb-1">Notes</p>
                                    <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                                        {selectedOrder.notes}
                                    </p>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-6">
                                <button
                                    onClick={() => {
                                        handleEditOrder(selectedOrder);
                                        setSelectedOrder(null);
                                    }}
                                    className="flex-1 bg-orange-600 text-white py-2 rounded-md hover:bg-orange-700 transition-colors"
                                >
                                    ✏️ Edit Order
                                </button>
                                <button
                                    className="flex-1 bg-blue-700 text-white py-2 rounded-md hover:bg-blue-800 transition-colors"
                                    onClick={() => setSelectedOrder(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderList;